import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../constants/colors';
import { useCart } from '../context/CartContext';
import { trackPurchase } from '../utils/metaPixel';

interface OrderDetails {
  transactionId: string;
  bookTitle: string;
  storyCount: number;
  deliveryEmail: string;
  orderStatus: string;
  estimatedDelivery: string;
}

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 16px;
  width: 100%;
  box-sizing: border-box;
  min-height: 70vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const SuccessCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 48px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  animation: scaleIn 0.5s ease-out;
  
  @keyframes scaleIn {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
  
  svg {
    width: 48px;
    height: 48px;
    color: white;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #212529;
  margin: 0 0 12px 0;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  color: #6c757d;
  margin: 0 0 32px 0;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const InfoSection = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 24px;
  margin: 24px 0;
  text-align: left;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e9ecef;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`;

const InfoLabel = styled.span`
  font-size: 0.938rem;
  color: #6c757d;
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 0.938rem;
  color: #212529;
  font-weight: 600;
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  background: ${props => {
    switch (props.$status) {
      case 'PAID': return '#d1fae5';
      case 'PRINTING': return '#fed7aa';
      case 'SHIPPED': return '#bfdbfe';
      case 'DELIVERED': return '#c7d2fe';
      default: return '#e5e7eb';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'PAID': return '#065f46';
      case 'PRINTING': return '#92400e';
      case 'SHIPPED': return '#1e40af';
      case 'DELIVERED': return '#4338ca';
      default: return '#374151';
    }
  }};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 32px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 14px 24px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  
  ${props => props.$variant === 'primary' ? `
    background: ${COLORS.PRIMARY};
    color: white;
    
    &:hover {
      background: #5568d3;
    }
  ` : `
    background: white;
    color: ${COLORS.PRIMARY};
    border: 2px solid ${COLORS.PRIMARY};
    
    &:hover {
      background: #f0f4ff;
    }
  `}
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #f3f4f6;
  border-top-color: ${COLORS.PRIMARY};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: 1rem;
  color: #6c757d;
`;

const EmailConfirmation = styled.div`
  background: #e0f2fe;
  border: 1px solid #7dd3fc;
  border-radius: 8px;
  padding: 16px;
  margin-top: 24px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  
  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: #0284c7;
  }
`;

const EmailText = styled.p`
  font-size: 0.875rem;
  color: #0c4a6e;
  margin: 0;
  line-height: 1.5;
`;

export const OrderSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { clearCart } = useCart();

  useEffect(() => {
    const sessionId = searchParams.get('session_id');

    if (!sessionId) {
      setError('Invalid order confirmation link');
      setLoading(false);
      return;
    }
    if (sessionId) {
      clearCart(); // Clear cart after successful payment
    }

    // Fetch order details from backend
    fetchOrderDetails(sessionId);
  }, [searchParams]);

  const fetchOrderDetails = async (sessionId: string) => {
    try {
      // Call your backend to get order details by session ID
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/books/order-by-session/${sessionId}`,
        {
          headers: {
            // Add auth token if needed
            // 'Authorization': `Bearer ${token}`
          }
        }
      );

      const result = await response.json();

      if (result.success && result.data) {
        const order = result.data;

        setOrderDetails({
          transactionId: order._id,
          bookTitle: order.metadata?.orderDetails?.bookTitle || 'Custom Story Book',
          storyCount: order.metadata?.storyIds?.length || 0,
          deliveryEmail: order.shippingAddress?.email || '',
          orderStatus: order.orderStatus || 'PAID',
          estimatedDelivery: calculateEstimatedDelivery(),
        });

        // Track purchase in Meta Pixel
        trackPurchase(
          order.metadata?.orderDetails?.bookTitle || 'Custom Story Book',
          (order.totalAmount || order.amount) / 100,
          order.currency?.toUpperCase() || 'EUR'
        );
      } else {
        setError('Failed to load order details');
      }
    } catch (err) {
      console.error('Error fetching order:', err);
      setError('An error occurred while loading your order');
    } finally {
      setLoading(false);
    }
  };

  const calculateEstimatedDelivery = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7); // 7 days from now
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <Spinner />
          <LoadingText>{t('orderSuccess.loading.text')}</LoadingText>
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error || !orderDetails) {
    return (
      <PageContainer>
        <SuccessCard>
          <Title>{t('orderSuccess.error.title')}</Title>
          <Subtitle>{error || t('orderSuccess.error.message')}</Subtitle>
          <Button $variant="primary" onClick={() => navigate('/')}>
            {t('orderSuccess.error.button')}
          </Button>
        </SuccessCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <SuccessCard>
        <IconContainer>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <polyline points="20 6 9 17 4 12" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </IconContainer>

        <Title>{t('orderSuccess.success.title')}</Title>
        <Subtitle>{t('orderSuccess.success.subtitle')}</Subtitle>

        <InfoSection>
          <InfoRow>
            <InfoLabel>{t('orderSuccess.success.infoSection.orderId')}</InfoLabel>
            <InfoValue>#{orderDetails.transactionId.slice(-8).toUpperCase()}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t('orderSuccess.success.infoSection.bookTitle')}</InfoLabel>
            <InfoValue>{orderDetails.bookTitle}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t('orderSuccess.success.infoSection.storyCount')}</InfoLabel>
            <InfoValue>
              { orderDetails.storyCount } {t('orderSuccess.success.infoSection.storyCountValue')}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t('orderSuccess.success.infoSection.orderStatus')}</InfoLabel>
            <StatusBadge $status={orderDetails.orderStatus}>
              {orderDetails.orderStatus}
            </StatusBadge>
          </InfoRow>
        </InfoSection>

        <EmailConfirmation>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="22,6 12,13 2,6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <EmailText
            dangerouslySetInnerHTML={{
              __html: `
      ${t('orderSuccess.success.emailConfirmation.text_1')} 
      <strong>${orderDetails.deliveryEmail}</strong> 
      ${t('orderSuccess.success.emailConfirmation.text_2')}
    `
            }}
          />
        </EmailConfirmation>

        <ButtonGroup>
          <Button $variant="secondary" onClick={() => navigate('/library')}>
            {t('orderSuccess.success.buttons.browseStories')}
          </Button>
          <Button $variant="primary" onClick={() => navigate('/')}>
            {t('orderSuccess.success.buttons.backHome')}
          </Button>
        </ButtonGroup>
      </SuccessCard>
    </PageContainer>
  );
};